import React, { memo, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  CardActionArea,
  Typography,
  Stack,
  IconButton,
  Box
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';
import PushPinIcon from '@mui/icons-material/PushPin';
import { pinedKey, idKey, dateKey, summaryKey, titleKey, isSummarizedKey } from './MeetingsKeysList';

/* ------------------------------
   Utils
------------------------------ */

// (DD/MM/YYYY HH:MM)
const formatCompactDate = (dateString) => {
  if (!dateString) return '';
  try {
    if (dateString.includes('GMT')) {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    if (dateString.includes('/')) {
      const parts = dateString.split(' ');
      if (parts.length >= 2) {
        const datePart = parts[0];
        const timePart = parts[1];
        const dateComponents = datePart.split('/');
        if (dateComponents.length === 3) {
          let year = dateComponents[2];
          if (year.length === 2) year = '20' + year;
          const newDatePart = `${dateComponents[0]}/${dateComponents[1]}/${year}`;
          const timeComponents = timePart.split(':');
          const newTimePart = timeComponents.slice(0, 2).join(':');
          return `${newDatePart} ${newTimePart}`;
        }
      }
    }
    return dateString;
  } catch {
    return dateString;
  }
};

const parseLoadingPercentage = (title) => {
  if (!title) return null;
  const percentageRegex = /[[(]?(\d{1,3})\s*%[\])]?/;
  const match = title.match(percentageRegex);
  if (match && match[1]) {
    const percentage = parseInt(match[1], 10);
    return percentage >= 0 && percentage <= 100 ? percentage : null;
  }
  return null;
};

const clamp01 = (v) => Math.max(0, Math.min(1, v));

/* ------------------------------
   Theme constants
------------------------------ */

const PROGRESS_ANIMATION = {
  STRIPE_PATTERN: {
    DETERMINATE: { CYCLE: 24 },   // keep integers to avoid seams
    INDETERMINATE: { CYCLE: 18 }
  },
  COLORS: {
    FILL: '#6f046f',
    BACKGROUND: 'rgba(0,0,0,0.1)',
    RIB_LIGHT: 'rgba(255,255,255,0.35)',
    RIB_DARK: 'rgba(255,255,255,0.10)'
  }
};

/* ------------------------------
   CHIProgressBar (seamless loop, paper-accurate speeds)
   - Fill grows L→R
   - Ribbing scrolls R→L and decelerates ~4:1 (exact paper speeds)
   - Seamless wrap, integer stops, pixel snapping, no React re-render per frame
------------------------------ */

const mmToPx = 96 / 25.4; // CSS 96dpi

// Global animation state store to persist across component re-renders
const globalAnimationStates = new Map();

// Cleanup function to remove animation states for meetings that are no longer processing
const cleanupAnimationState = (meetingId) => {
  if (globalAnimationStates.has(meetingId)) {
    globalAnimationStates.delete(meetingId);
  }
};

// Memoized progress bar component that persists animation state across re-renders
const CHIProgressBar = memo(({ percentage, isIndeterminate = false, meetingId }) => {

  // Integer-friendly cycle and stops to avoid tiny seams on GPUs
  const CYCLE = isIndeterminate
    ? PROGRESS_ANIMATION.STRIPE_PATTERN.INDETERMINATE.CYCLE
    : PROGRESS_ANIMATION.STRIPE_PATTERN.DETERMINATE.CYCLE;

  const L = 8;                         // light band width (px) — integer
  const D = CYCLE - L;                 // dark band width (px)
  const STRIPE_LIGHT = PROGRESS_ANIMATION.COLORS.RIB_LIGHT;
  const STRIPE_DARK  = PROGRESS_ANIMATION.COLORS.RIB_DARK;
  const FILL_COLOR   = PROGRESS_ANIMATION.COLORS.FILL;

  // Paper-accurate speeds (–31.3 → –7.8 mm/s); negative = right→left
  const START_SPEED = -(31.3 * mmToPx); // ≈ -118.9 px/s (fast at 0%)
  const END_SPEED   = -(7.8  * mmToPx); // ≈  -29.5 px/s (slow at 100%)

  const reduceMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const progress01 = clamp01((percentage ?? 0) / 100);
  const targetSpeed = isIndeterminate
    ? (START_SPEED + END_SPEED) / 2
    : START_SPEED + (END_SPEED - START_SPEED) * progress01;

  const stripesRef = useRef(null);
  const rafRef = useRef(0);
  
  // Get or create persistent animation state for this meeting
  const getAnimationState = useCallback(() => {
    if (!globalAnimationStates.has(meetingId)) {
      globalAnimationStates.set(meetingId, {
        x: 0,
        v: START_SPEED,
        lastTime: typeof performance !== 'undefined' ? performance.now() : Date.now(),
        targetSpeed: START_SPEED
      });
    }
    return globalAnimationStates.get(meetingId);
  }, [meetingId, START_SPEED]);

  // Update target speed in persistent state
  useEffect(() => {
    const animState = getAnimationState();
    animState.targetSpeed = targetSpeed;
  }, [targetSpeed, getAnimationState]);

  // true modulo mapped to (-CYCLE, 0] to avoid jumps across 0
  const wrap = useMemo(() => (x) => {
    const m = ((x % CYCLE) + CYCLE) % CYCLE; // [0, CYCLE)
    return m === 0 ? 0 : m - CYCLE;          // (-CYCLE, 0]
  }, [CYCLE]);

  useEffect(() => {
    if (reduceMotion || !stripesRef.current) return;
    
    const animState = getAnimationState();
    
    // Cancel any existing animation for this component instance
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    const step = 1 / (window.devicePixelRatio || 1); // snap to device pixel grid

    const tick = (now) => {
      const currentTime = now ?? Date.now();
      const dt = Math.max(0, Math.min(0.1, (currentTime - animState.lastTime) / 1000));
      animState.lastTime = currentTime;

      // critically-damped-ish smoothing to avoid jumps when progress updates in steps
      const k = 10;
      animState.v += (animState.targetSpeed - animState.v) * (1 - Math.exp(-k * dt));

      animState.x = wrap(animState.x + animState.v * dt);

      // pixel snapping reduces shimmer on diagonals at high DPI
      const snapped = Math.round(animState.x / step) * step;

      // mutate DOM directly (no React state re-render)
      if (stripesRef.current) {
        stripesRef.current.style.backgroundPosition = `${snapped}px 0px`;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion, CYCLE, wrap, getAnimationState, meetingId]);

  const widthPct = isIndeterminate ? 100 : progress01 * 100;

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Fill layer resizes; stripe layer scrolls independently */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          width: `${widthPct}%`,
          backgroundColor: FILL_COLOR,
          transition: isIndeterminate ? undefined : 'width 150ms linear',
          overflow: 'hidden',
          willChange: 'width'
        }}
      >
        <Box
          ref={stripesRef}
          sx={{
            position: 'absolute',
            inset: 0,
            transform: 'translateZ(0)',
            willChange: 'background-position',
            backgroundImage: `
              repeating-linear-gradient(
                135deg,
                ${STRIPE_LIGHT} 0px,
                ${STRIPE_LIGHT} ${L}px,
                ${STRIPE_DARK}  ${L}px,
                ${STRIPE_DARK}  ${L + D}px
              )
            `,
            backgroundSize: `${CYCLE}px ${CYCLE}px`,
            backgroundRepeat: 'repeat',
            backgroundPosition: '0px 0px'
          }}
        />
      </Box>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Always re-render if meetingId changes (different meeting)
  if (prevProps.meetingId !== nextProps.meetingId) {
    return false;
  }
  
  // Only re-render if percentage or indeterminate state changes significantly
  if (prevProps.isIndeterminate !== nextProps.isIndeterminate) {
    return false;
  }
  
  if (prevProps.percentage !== nextProps.percentage) {
    // For small percentage changes, avoid re-render to maintain smooth animation
    if (prevProps.percentage !== null && nextProps.percentage !== null) {
      const diff = Math.abs(prevProps.percentage - nextProps.percentage);
      return diff < 1; // Only re-render for changes >= 1%
    }
    return false; // Re-render if one is null and the other isn't
  }
  
  return true; // No significant change, don't re-render
});

/* ------------------------------
   Meeting Card
------------------------------ */

// Create a more specific comparison function for React.memo
const arePropsEqual = (prevProps, nextProps) => {
  // Quick reference check - if meeting objects are the same reference, likely no change
  if (prevProps.meeting === nextProps.meeting && 
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.onSelect === nextProps.onSelect &&
      prevProps.onDelete === nextProps.onDelete &&
      prevProps.onTogglePin === nextProps.onTogglePin &&
      prevProps.onShare === nextProps.onShare) {
    return true; // No change at all
  }

  // Compare meeting data that affects rendering
  const prevMeeting = prevProps.meeting;
  const nextMeeting = nextProps.meeting;
  
  // Early return if meeting ID changed (different meeting entirely)
  if (prevMeeting[idKey] !== nextMeeting[idKey]) {
    return false;
  }
  
  // Check if callback functions changed (they shouldn't if properly memoized)
  const callbacksChanged = (
    prevProps.onSelect !== nextProps.onSelect ||
    prevProps.onDelete !== nextProps.onDelete ||
    prevProps.onTogglePin !== nextProps.onTogglePin ||
    prevProps.onShare !== nextProps.onShare
  );
  
  if (callbacksChanged) {
    return false;
  }
  
  // Check selection state
  if (prevProps.isSelected !== nextProps.isSelected) {
    return false;
  }
  
  // For processing meetings, check if loading percentage meaningfully changed
  const prevIsProcessing = prevMeeting[isSummarizedKey] === 0;
  const nextIsProcessing = nextMeeting[isSummarizedKey] === 0;
  
  // If processing state changed, re-render
  if (prevIsProcessing !== nextIsProcessing) {
    return false;
  }
  
  // If both are processing, check percentage change
  if (prevIsProcessing && nextIsProcessing) {
    const prevPercentage = parseLoadingPercentage(prevMeeting[titleKey]);
    const nextPercentage = parseLoadingPercentage(nextMeeting[titleKey]);
    
    // Only re-render if percentage changed significantly
    if (prevPercentage !== nextPercentage) {
      if ((prevPercentage === null) !== (nextPercentage === null)) {
        return false;
      }
      if (prevPercentage !== null && nextPercentage !== null) {
        const percentageDiff = Math.abs(prevPercentage - nextPercentage);
        if (percentageDiff >= 1) {
          return false;
        }
      }
    }
    
    // For processing meetings, only check title for percentage changes
    // Other properties shouldn't change during processing
    return true; // Skip other property checks for processing meetings
  }
  
  // Check other essential properties (only for non-processing meetings)
  const essentialPropsEqual = (
    prevMeeting[titleKey] === nextMeeting[titleKey] &&
    prevMeeting[dateKey] === nextMeeting[dateKey] &&
    prevMeeting[summaryKey] === nextMeeting[summaryKey] &&
    prevMeeting[pinedKey] === nextMeeting[pinedKey]
  );
  
  if (!essentialPropsEqual) {
    return false;
  }
  
  return true;
};

const MeetingCard = ({
  meeting,
  isSelected,
  onSelect,
  onDelete,
  onTogglePin,
  onShare
}) => {
  const meetingId = meeting[idKey];
  const backgroundColor = isSelected ? 'var(--meetingCardBackground)' : 'white';

  const isProcessing = meeting[isSummarizedKey] === 0;
  const loadingPercentage = isProcessing ? parseLoadingPercentage(meeting[titleKey]) : null;
  
  // Cleanup animation state when meeting finishes processing
  useEffect(() => {
    if (!isProcessing) {
      cleanupAnimationState(meetingId);
    }
  }, [isProcessing, meetingId]);

  const handleCardClick  = () => onSelect(meeting);
  const handleDeleteClick = (e) => { e.stopPropagation(); onDelete(meetingId); };
  const handlePinClick    = (e) => { e.stopPropagation(); onTogglePin(meetingId, meeting[pinedKey]); };
  const handleShareClick  = (e) => { e.stopPropagation(); onShare(meetingId); };

  return (
    <Stack direction="row" spacing={0} style={{ height: '150px', marginLeft: '8px', marginRight: '8px' }}>
      {/* Delete button card */}
      <Card
        orientation="horizontal"
        style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'flex-end',
          backgroundColor,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0
        }}
      >
        <CardActions style={{ justifyContent: 'center', alignItems: 'center' }} orientation="vertical">
          <IconButton
            aria-label="delete meeting"
            onClick={handleDeleteClick}
            style={{
              zIndex: '2',
              color: 'var(--buttonColor)',
              opacity: 0.5,
              transition: 'color 0.2s ease, opacity 0.2s ease'
            }}
            sx={{ '&:hover': { color: 'var(--red)', opacity: 1 } }}
          >
            <DeleteIcon id="deleteIcon" />
          </IconButton>
        </CardActions>
      </Card>

      {/* Main content card */}
      <Card
        orientation="horizontal"
        style={{
          zIndex: 1,
          width: '90%',
          height: '150px',
          backgroundColor,
          borderRadius: 0,
          position: 'relative'
        }}
      >
        <CardActionArea style={{ zIndex: 1, width: '100%', height: '100%' }} onClick={handleCardClick}>
          <CardContent>
            <Typography
              variant="h6"
              component="div"
              style={{
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                fontWeight: isSelected ? 'bold' : 'normal'
              }}
            >
              {meeting[titleKey]}
            </Typography>

            <Typography sx={{ mb: 1.5 }} color="text.secondary">
              {formatCompactDate(meeting[dateKey])}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2
              }}
            >
              {String(meeting[summaryKey] || '')
                .replaceAll('*', '')
                .replaceAll('#', '')
                .replaceAll('~', '')
                .replaceAll('\\', '')
                }
            </Typography>
          </CardContent>

          {/* Progress bar at bottom */}
          {isProcessing && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 4,
                backgroundColor: PROGRESS_ANIMATION.COLORS.BACKGROUND,
                overflow: 'hidden'
              }}
            >
              <CHIProgressBar
                meetingId={meetingId}
                percentage={loadingPercentage}
                isIndeterminate={loadingPercentage === null}
              />
            </Box>
          )}
        </CardActionArea>
      </Card>

      {/* Action buttons card */}
      <Card
        orientation="horizontal"
        style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'flex-end',
          backgroundColor,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0
        }}
      >
        <CardActions style={{ justifyContent: 'flex-end' }} orientation="vertical">
          <Stack direction="column" spacing={2} justifyContent="space-between">
            <IconButton
              aria-label="pin meeting"
              onClick={handlePinClick}
              style={{
                zIndex: '2',
                color: 'var(--buttonColor)',
                opacity: meeting[pinedKey] ? 1 : 0.5,
                transition: 'color 0.2s ease, opacity 0.2s ease, transform 0.2s ease'
              }}
              sx={{
                '& .MuiSvgIcon-root': {
                  transform: meeting[pinedKey] ? 'rotate(0deg)' : 'rotate(45deg)',
                  transition: 'transform 0.2s ease'
                },
                '&:hover': {
                  opacity: 1,
                  '& .MuiSvgIcon-root': { transform: 'rotate(0deg)' }
                }
              }}
            >
              <PushPinIcon />
            </IconButton>

            <IconButton
              aria-label="share meeting"
              onClick={handleShareClick}
              style={{
                zIndex: '2',
                color: 'var(--buttonColor)',
                opacity: 0.5,
                transition: 'color 0.2s ease, opacity 0.2s ease'
              }}
              sx={{ '&:hover': { opacity: 1 } }}
            >
              <ShareIcon />
            </IconButton>
          </Stack>
        </CardActions>
      </Card>
    </Stack>
  );
};

MeetingCard.displayName = 'MeetingCard';

export { cleanupAnimationState };
export default memo(MeetingCard, arePropsEqual);
